<template>
  <div 
    class="group-section"
    :class="{ 'break-inside-avoid': true }"
  >
    <GroupHeader
      :group-name="group.name"
      :group-data="groupData"
      :show-images="showImages"
      :shared="shared"
      :can-edit-groups="canEditGroups"
      :can-delete-groups="canDeleteGroups"
      :can-reorder-groups="canReorderGroups"
      :is-first="isFirst"
      :is-last="isLast"
      :group-image-style="groupImageStyle"
      @edit="$emit('editGroup', $event)"
      @delete="$emit('deleteGroup', $event)"
      @moveUp="$emit('moveGroupUp', $event)"
      @moveDown="$emit('moveGroupDown', $event)"
    />
    
    <draggable
      v-model="group.items"
      group="items"
      @change="$emit('itemReorder')"
      item-key="id"
      tag="ul"
      class="items-list"
      handle=".drag-handle"
      :disabled="shared && !canReorderItems"
      ghost-class="drag-ghost"
    >
      <template #item="{ element: item }">
        <ItemRow
          :item="item"
          :calculated-quantity="getCalculatedQuantity(item)"
          :shared="shared"
          :can-edit-items="canEditItems"
          :can-delete-items="canDeleteItems"
          :can-reorder-items="canReorderItems"
          @update-check="$emit('updateItemCheck', $event)"
          @edit="$emit('editItem', $event)"
          @delete="$emit('deleteItem', $event)"
          @retry="$emit('retryItem', $event)"
          @remove="$emit('removeItem', $event)"
        />
      </template>
    </draggable>
    
    <!-- Quick add item field at the end of each group -->
    <QuickAddItem
      v-if="!shared || canAddItems"
      :group-name="group.name || 'General'"
      :model-value="quickAddValue"
      :is-loading="quickAddLoading"
      @update:model-value="$emit('update:quickAddValue', $event)"
      @add="$emit('quickAdd', $event)"
      @add-and-stay="handleQuickAddAndStay"
    />
  </div>
</template>

<script>
import GroupHeader from './GroupHeader.vue'
import ItemRow from './ItemRow.vue'
import QuickAddItem from './QuickAddItem.vue'
import draggable from 'vuedraggable'

export default {
  name: 'GroupSection',
  components: {
    GroupHeader,
    ItemRow,
    QuickAddItem,
    draggable
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    groupData: {
      type: Object,
      default: null
    },
    showImages: {
      type: Boolean,
      default: true
    },
    shared: {
      type: Boolean,
      default: false
    },
    groupImageStyle: {
      type: Object,
      default: () => ({})
    },
    getCalculatedQuantity: {
      type: Function,
      required: true
    },
    quickAddValue: {
      type: String,
      default: ''
    },
    quickAddLoading: {
      type: Boolean,
      default: false
    },
    canEditGroups: {
      type: Boolean,
      default: false
    },
    canAddItems: {
      type: Boolean,
      default: false
    },
    canEditItems: {
      type: Boolean,
      default: false
    },
    canDeleteItems: {
      type: Boolean,
      default: false
    },
    canDeleteGroups: {
      type: Boolean,
      default: false
    },
    canReorderItems: {
      type: Boolean,
      default: false
    },
    canReorderGroups: {
      type: Boolean,
      default: false
    },
    isFirst: {
      type: Boolean,
      default: false
    },
    isLast: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'editGroup',
    'deleteGroup',
    'moveGroupUp',
    'moveGroupDown',
    'itemReorder',
    'updateItemCheck',
    'editItem',
    'deleteItem',
    'quickAdd',
    'quickAddAndStay',
    'update:quickAddValue',
    'retryItem',
    'removeItem'
  ],
  methods: {
    handleQuickAddAndStay(groupName, event) {
      this.$emit('quickAddAndStay', groupName, event)
    }
  }
}
</script>

<style scoped>
.group-section {
  margin-bottom: 1rem;
}

.items-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Default compact styles */
.group-section {
  margin-bottom: 0.75rem;
}

/* Compact sizing for 4-column layout */
@media screen {
  .columns-4 .group-section {
    margin-bottom: 0.3rem;
  }
}

/* Print styles */
@media print {
  .group-section {
    margin-bottom: 0.8rem;
  }
  
  .columns-4 .group-section {
    margin-bottom: 0.2rem;
    break-inside: auto;
    page-break-inside: auto;
  }
  
  /* Allow groups to flow across columns */
  .group-section {
    margin-bottom: 0.8rem;
  }
}

/* Hide drag ghost element */
.drag-ghost {
  opacity: 0 !important;
  visibility: hidden !important;
}
</style>